import { T, amountStyle } from '../theme';
import ExpenseList from './ExpenseList';
import { FinanceViewTopBar, ReportBar } from './NavShared';
import { Download, Plus, ArrowLeft } from 'lucide-react';
import { formatAmount } from '../utils/formatters';

export default function ExpenseListView({
  user,
  totalSpend,
  expenses,
  onExpenseClick,
  onDeleteExpense,
  defaultCurrency,
  adjustmentFactor,
  ratesLoading,
  ratesError,
  isShared,
  tier,
  scansUsed,
  totalScansAllowed,
  reportName,
  reportType,
  currentFileAccess,
  onAddExpense,
  bulkCatOffer,
  onApplyBulkCat,
  onDismissBulkCat,
  saveMsg,
  autoSaving,
  onShareClick,
  onLoadClick,
  onExportClick,
  onCreateReviewLinkClick,
  topBarProps,
  onGoToDashboard,
  onGoToAdd,
}) {
  const adjNum = parseFloat(adjustmentFactor) || 0;

  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column' }}>

      {/* Compact header */}
      <div style={{ background: T.gradNavy, padding: '14px 16px 10px', flexShrink: 0 }}>
        {topBarProps && <FinanceViewTopBar {...topBarProps} />}
        {onGoToDashboard && (
          <button
            onClick={onGoToDashboard}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
          >
            <ArrowLeft size={14} color="#fff" /> Dashboard
          </button>
        )}

        {/* Report name row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
          <ReportBar
            currentFileName={reportName}
            autoSaving={autoSaving}
            currentFileAccess={currentFileAccess}
            onShareClick={onShareClick}
            onLoadClick={onLoadClick}
            onCreateReviewLinkClick={onCreateReviewLinkClick}
          />
          {onExportClick && (
            <button
              onClick={onExportClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'rgba(255,255,255,0.15)', border: 'none',
                borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}
            >
              <Download size={11} /> Export
            </button>
          )}
        </div>

        {/* Amount + meta row */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 5 }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: '#fff', lineHeight: 1.1, ...amountStyle({ letterSpacing: -0.5 }) }}>
            {ratesLoading
              ? <span style={{ fontSize: 18, opacity: 0.7 }}>Converting…</span>
              : formatAmount(totalSpend, defaultCurrency)
            }
          </span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.3 }}>
            {expenses.length} item{expenses.length !== 1 ? 's' : ''}
            {user && tier && totalScansAllowed != null && (
              <> · {scansUsed}/{totalScansAllowed === Infinity ? '∞' : totalScansAllowed} scans</>
            )}
          </span>
        </div>
        {adjNum !== 0 && (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
            non-{defaultCurrency} adjusted {adjNum > 0 ? '+' : ''}{adjNum}%
          </div>
        )}
        {ratesError && (
          <div style={{ fontSize: 11, color: 'rgba(255,200,100,0.9)', marginTop: 2 }}>
            ⚠ Exchange rates unavailable — amounts shown unconverted
          </div>
        )}
      </div>

      {/* Save message */}
      {saveMsg && (
        <div style={{ background: '#e6da32ec', padding: '12px 16px', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          {saveMsg}
        </div>
      )}

      {/* Scrollable expense list */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 80 }}>
        <ExpenseList
          expenses={expenses}
          onExpenseClick={onExpenseClick}
          onDeleteExpense={onDeleteExpense}
          defaultCurrency={defaultCurrency}
          isShared={isShared}
          reportName={reportName}
        />
      </div>

      {currentFileAccess !== 'view' && onGoToAdd && (
        <button
          onClick={onGoToAdd}
          title="Add expense"
          style={{
            position: 'fixed', bottom: 24, right: 20, zIndex: 100,
            width: 52, height: 52, borderRadius: 26, border: 'none',
            background: T.gradTeal, color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          }}
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}

      {/* Bulk category offer */}
      {bulkCatOffer && (
        <div style={{
          position: 'fixed', bottom: 84, left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
          zIndex: 99, padding: '0 16px', pointerEvents: 'none',
        }}>
          <div style={{
            background: T.navy, color: '#fff', borderRadius: 14,
            padding: '12px 16px', maxWidth: 440, width: '100%',
            display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            pointerEvents: 'all',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {bulkCatOffer.peerIds.length} other {bulkCatOffer.vendor} expense{bulkCatOffer.peerIds.length !== 1 ? 's' : ''} in this report
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                Set category to <strong style={{ color: '#fff' }}>{bulkCatOffer.category}</strong>?
              </div>
            </div>
            <button
              onClick={onApplyBulkCat}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: T.gradTeal, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Apply to all
            </button>
            <button
              onClick={onDismissBulkCat}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)', background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
